import React, { useState, useEffect } from 'react'
import { Button, Alert, Box, Typography } from '@mui/material'
import OutdatedQuestionsFeedbackModal from './OutdatedQuestionsFeedbackModal'
import { getScanResultId } from '../api/applicationsAPI'

interface OutdatedQuestionsFeedbackButtonProps {
  token: string
  orgUuid: string
  needId: number
  opportunityUuid: string
}

const OutdatedQuestionsFeedbackButton: React.FC<
  OutdatedQuestionsFeedbackButtonProps
> = ({ token, orgUuid, needId, opportunityUuid }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [scanResultId, setScanResultId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const handleOpenModal = async () => {
    setIsLoading(true)
    setSuccessMessage(null)

    try {
      // Try to find the scan_result_id
      const resultId = await getScanResultId(
        token,
        orgUuid,
        needId,
        opportunityUuid,
      )

      if (resultId === null) {
        // If we can't find the scan result, still allow submission with null
        console.warn(
          'Could not find scan result, will submit as general feedback',
        )
      }

      setScanResultId(resultId)
      setIsModalOpen(true)
    } catch (err) {
      // This catch block will only handle unexpected errors from the client-side code itself
      console.error('An unexpected error occurred in handleOpenModal')
      // Still allow modal to open with null scan result
      setScanResultId(null)
      setIsModalOpen(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setScanResultId(null)
  }

  const handleFeedbackSubmitted = () => {
    setSuccessMessage(
      'Thank you for your feedback! We will look into this and update the questions.',
    )
  }

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 7000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  return (
    <Box>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Button
        disableRipple
        onClick={handleOpenModal}
        disabled={isLoading}
        sx={{
          textTransform: 'none',
          fontSize: '14px',
          color: '#1C3045',
          textDecoration: 'underline',
          p: 0,
          minWidth: 'auto',
          '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
          },
          '&:focus': {
            backgroundColor: '#e8e8e8',
          },
        }}
      >
        {isLoading ? (
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            Loading...
          </Typography>
        ) : (
          'Funder updated their questions? Let us know'
        )}
      </Button>

      <OutdatedQuestionsFeedbackModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        scanResultId={scanResultId}
        orgUuid={orgUuid}
        token={token}
        onFeedbackSubmitted={handleFeedbackSubmitted}
      />
    </Box>
  )
}

export default OutdatedQuestionsFeedbackButton
